import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const insightStatusSchema = z.enum(["PENDING", "SUCCESS", "FAILED", "CANCELLED", "RETRY", "IN_PROGRESS", "REJECTED"]);

const insightSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  metadata: z.record(z.unknown()).optional(),
  marketplace: z.string().nullable().optional(),
  entity_type: z.string(),
  entity_id: z.number(),
  entity: z.record(z.unknown()).optional(),
  is_viewed: z.boolean(),
  is_dismissed: z.boolean(),
  created_on: z.string().datetime(),
  status: insightStatusSchema.nullable().optional(),
});

export type Insight = z.infer<typeof insightSchema>;

const insightViewSchema = z.object({
  id: z.number(),
  insight: z.number(),
  created_on: z.string().datetime(),
});

const insightDismissalSchema = z.object({
  id: z.number(),
  insight: z.number(),
  created_on: z.string().datetime(),
});

const paginatedInsightsResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(insightSchema),
});

export const insightsApiDefinitions = prefixApi(
  "/api/chat",
  apiBuilder({
    alias: "listInsights",
    method: "get",
    path: "/insights/",
    parameters: [
      {
        name: "dismissed",
        type: "Query",
        schema: z.enum(["true", "false"]).optional(),
      },
      {
        name: "viewed",
        type: "Query",
        schema: z.enum(["true", "false"]).optional(),
      },
      {
        name: "entity_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "entity_id",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: insightStatusSchema.optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().min(1).optional(),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z.number().min(1).max(100).optional(),
      },
    ],
    status: 200,
    response: paginatedInsightsResponseSchema,
  })
    .addEndpoint({
      alias: "markInsightAsViewed",
      method: "post",
      path: "/insights/:id/view/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
      ],
      status: 201,
      response: insightViewSchema,
    })
    .addEndpoint({
      alias: "dismissInsight",
      method: "post",
      path: "/insights/:id/dismiss/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
      ],
      status: 201,
      response: insightDismissalSchema,
    })
    .build(),
);
